import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live WhatsApp-style preview + inline editor + drafts + version history.
 */
export default function TemplatePreviewEditor({ preset, token, onClose, onSubmitted }) {
  const [bodyText, setBodyText] = useState(preset.body_text || '');
  const [footerText, setFooterText] = useState(preset.footer_text || '');
  const [name, setName] = useState(preset.name || '');
  const [examples, setExamples] = useState(preset.example_values || []);
  const [buttons, setButtons] = useState(preset.buttons || []);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [result, setResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [noteText, setNoteText] = useState('');

  const [draft, setDraft] = useState(null);          // current saved draft
  const [versions, setVersions] = useState([]);      // version history
  const [showVersions, setShowVersions] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  // Load existing draft + versions on mount
  useEffect(() => {
    loadDraft();
    // eslint-disable-next-line
  }, [preset.id]);

  const loadDraft = async () => {
    setLoadingDraft(true);
    try {
      const r = await fetch(`${API_URL}/api/whatsapp/templates/drafts?preset_id=${preset.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (data?.drafts?.length) {
        const d = data.drafts[0];
        setDraft(d);
        // Ask user: load draft or keep preset?
        setBodyText(d.body_text || preset.body_text);
        setFooterText(d.footer_text || preset.footer_text || '');
        setName(d.name || preset.name);
        setExamples(d.body_example || preset.example_values || []);
        setButtons(d.buttons || preset.buttons || []);
        loadVersions(d.id);
      } else {
        setBodyText(preset.body_text || '');
        setFooterText(preset.footer_text || '');
        setName(preset.name || '');
        setExamples(preset.example_values || []);
        setButtons(preset.buttons || []);
      }
    } catch (e) { console.error(e); }
    setLoadingDraft(false);
  };

  const loadVersions = async (draftId) => {
    try {
      const r = await fetch(`${API_URL}/api/whatsapp/templates/drafts/${draftId}/versions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setVersions(data?.versions || []);
    } catch (e) { console.error(e); }
  };

  const renderPreview = (text) => {
    let out = text;
    examples.forEach((v, idx) => {
      out = out.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g'), v || `_${idx + 1}_`);
    });
    return out;
  };

  const nowTime = () => {
    const d = new Date();
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/whatsapp/templates/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          preset_id: preset.id, name, body_text: bodyText, footer_text: footerText,
          buttons, body_example: examples, category: preset.category, language: 'en_US',
          note: noteText || 'Manual save',
        }),
      });
      const data = await r.json();
      setResult({ success: data.success, message: `💾 ${data.message || 'Draft saved'} (v${data.version_num})` });
      if (data.success) {
        await loadDraft();
        setNoteText('');
      }
    } catch (e) { setResult({ success: false, message: 'Save failed: ' + e.message }); }
    setSavingDraft(false);
  };

  const restoreVersion = async (versionNum) => {
    if (!draft || !window.confirm(`v${versionNum} को restore करें? Current version history में save हो जाएगा।`)) return;
    try {
      const r = await fetch(`${API_URL}/api/whatsapp/templates/drafts/${draft.id}/restore/${versionNum}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (data.success) {
        setResult({ success: true, message: `🔄 v${versionNum} restored as new version` });
        await loadDraft();
        setShowVersions(false);
      }
    } catch (e) { setResult({ success: false, message: 'Restore failed' }); }
  };

  const submitToMeta = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      // Save draft first, then submit it
      const saveRes = await fetch(`${API_URL}/api/whatsapp/templates/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          preset_id: preset.id, name, body_text: bodyText, footer_text: footerText,
          buttons, body_example: examples, category: preset.category, language: 'en_US',
          note: noteText || 'Auto-save before Meta submit',
        }),
      });
      const saveData = await saveRes.json();
      const draftId = saveData.draft_id;

      const r = await fetch(`${API_URL}/api/whatsapp/templates/drafts/${draftId}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setResult(data);
      if (data?.success) {
        await loadDraft();
        if (onSubmitted) onSubmitted(data);
      }
    } catch (e) {
      setResult({ success: false, error: e.message });
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="template-preview-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between flex-wrap gap-2 z-10">
          <div>
            <h2 className="text-xl font-black text-slate-900">📱 Template Preview & Edit</h2>
            <p className="text-sm text-slate-500">
              <span className="font-mono">{preset.id}</span> · {preset.category}
              {draft && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-semibold">v{draft.version_num} saved</span>}
              {draft?.submitted_to_meta && <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold">✓ Submitted</span>}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {versions.length > 0 && (
              <button
                onClick={() => setShowVersions(!showVersions)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                data-testid="btn-toggle-versions"
              >
                📜 History ({versions.length})
              </button>
            )}
            <button
              onClick={() => setEditMode(!editMode)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${editMode ? 'bg-yellow-100 text-yellow-800' : 'bg-slate-100 text-slate-700'}`}
              data-testid="btn-toggle-edit-mode"
            >
              {editMode ? '✓ Editing' : '✏️ Edit'}
            </button>
            <button onClick={onClose} className="text-2xl text-slate-400 hover:text-slate-700 leading-none px-2" data-testid="btn-close-preview">×</button>
          </div>
        </div>

        {/* Version History Panel */}
        {showVersions && versions.length > 0 && (
          <div className="bg-purple-50 border-b border-purple-200 px-6 py-3 max-h-52 overflow-y-auto" data-testid="version-history-panel">
            <h4 className="font-bold text-sm text-purple-900 mb-2">📜 Version History</h4>
            <div className="space-y-1.5">
              {versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-3 p-2 bg-white rounded border border-purple-100 text-xs" data-testid={`version-row-${v.version_num}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-purple-700">v{v.version_num}</span>
                      <span className="text-slate-500">{new Date(v.saved_at).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-600 text-[10px] truncate">{v.note || 'No note'}</p>
                  </div>
                  <button
                    onClick={() => restoreVersion(v.version_num)}
                    className="px-3 py-1 bg-purple-500 text-white rounded-full text-[10px] font-bold hover:bg-purple-600"
                    data-testid={`btn-restore-v${v.version_num}`}
                  >
                    🔄 Restore
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* LEFT: Live preview */}
          <div className="space-y-3">
            <h3 className="font-bold text-slate-700">📲 Live WhatsApp Preview</h3>
            <div className="bg-[#e5ddd5] rounded-2xl p-6 min-h-[400px]">
              <div className="bg-white rounded-2xl shadow-md max-w-md p-3 relative" data-testid="wa-preview-bubble">
                <p className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">
                  {renderPreview(bodyText).split(/(\*[^*]+\*)/g).map((chunk, i) =>
                    chunk.startsWith('*') && chunk.endsWith('*') && chunk.length > 2
                      ? <strong key={i}>{chunk.slice(1, -1)}</strong>
                      : <span key={i}>{chunk}</span>
                  )}
                </p>
                {footerText && <p className="text-xs text-slate-400 mt-2">{footerText}</p>}
                <p className="text-[10px] text-slate-400 text-right mt-1">{nowTime()} ✓✓</p>
                {buttons && buttons.length > 0 && (
                  <div className="mt-2 -mx-3 -mb-3 border-t border-slate-100 rounded-b-2xl overflow-hidden">
                    {buttons.map((b, i) => (
                      <div key={i} className="px-4 py-2.5 text-center text-sm font-medium text-[#00a884] border-b border-slate-100 last:border-b-0">
                        🔗 {b.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              💡 <strong>Variables:</strong> {`{{1}} = ${examples[0] || 'N/A'}, {{2}} = ${examples[1] || 'N/A'}${examples[2] ? `, {{3}} = ${examples[2]}` : ''}`}
            </div>
          </div>

          {/* RIGHT: Editor */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-700">⚙️ Template Configuration</h3>

            <div>
              <label className="text-xs font-semibold text-slate-600">Template Name (Meta identifier)</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)} disabled={!editMode}
                className="w-full mt-1 px-3 py-2 border rounded-lg font-mono text-sm disabled:bg-slate-50 disabled:text-slate-500"
                data-testid="input-template-name"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Body Text</label>
              <textarea
                value={bodyText} onChange={e => setBodyText(e.target.value)} disabled={!editMode} rows={10}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono resize-none disabled:bg-slate-50 disabled:text-slate-500"
                data-testid="textarea-body"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">Use {`{{1}}, {{2}}`} for variables. `*text*` for bold.</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Footer (optional)</label>
              <input
                type="text" value={footerText} onChange={e => setFooterText(e.target.value)} disabled={!editMode}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-500"
                data-testid="input-footer"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600">Example Variables</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {examples.map((ex, i) => (
                  <input
                    key={i} type="text" value={ex}
                    onChange={e => { const ne = [...examples]; ne[i] = e.target.value; setExamples(ne); }}
                    disabled={!editMode}
                    placeholder={`{{${i + 1}}}`}
                    className="px-3 py-2 border rounded-lg text-sm disabled:bg-slate-50"
                    data-testid={`input-example-${i}`}
                  />
                ))}
              </div>
            </div>

            {buttons.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Buttons</label>
                <div className="space-y-2 mt-1">
                  {buttons.map((b, i) => (
                    <div key={i} className="flex gap-2 items-center bg-slate-50 rounded-lg p-2 text-sm">
                      <span className="text-xs text-slate-500 w-10">{b.type}</span>
                      <input
                        type="text" value={b.text || ''}
                        onChange={e => { const nb = [...buttons]; nb[i] = { ...nb[i], text: e.target.value }; setButtons(nb); }}
                        disabled={!editMode}
                        className="flex-1 px-2 py-1 border rounded text-xs"
                        placeholder="Button Text"
                      />
                      {b.type === 'URL' && (
                        <input
                          type="url" value={b.url || ''}
                          onChange={e => { const nb = [...buttons]; nb[i] = { ...nb[i], url: e.target.value }; setButtons(nb); }}
                          disabled={!editMode}
                          className="flex-[2] px-2 py-1 border rounded text-xs font-mono"
                          placeholder="https://..."
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {editMode && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Save Note (optional — history में दिखेगा)</label>
                <input
                  type="text" value={noteText} onChange={e => setNoteText(e.target.value)}
                  placeholder={'जैसे: "Fixed pricing contradiction"'}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="input-note"
                />
              </div>
            )}

            {result && (
              <div className={`rounded-lg p-3 text-sm ${result.success !== false ? 'bg-green-50 text-green-900 border border-green-200' : 'bg-red-50 text-red-900 border border-red-200'}`} data-testid="action-result">
                {result.success !== false ? <div>✅ {result.message || 'Submitted to Meta — 1-24 hrs for approval'}</div> : <div>❌ {result.detail || result.error || result.message || 'Failed'}</div>}
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t flex-wrap">
              <button
                onClick={saveDraft}
                disabled={savingDraft || !editMode}
                className="flex-1 min-w-[140px] px-4 py-2.5 bg-blue-500 text-white rounded-full font-bold hover:bg-blue-600 disabled:opacity-40"
                data-testid="btn-save-draft"
              >
                {savingDraft ? '⏳ Saving...' : '💾 Save Draft'}
              </button>
              <button
                onClick={submitToMeta}
                disabled={submitting}
                className="flex-1 min-w-[140px] px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full font-bold hover:from-orange-600 hover:to-red-600 disabled:opacity-50"
                data-testid="btn-submit-template"
              >
                {submitting ? '⏳ Submit...' : '📤 Submit to Meta'}
              </button>
              <button onClick={onClose} className="px-4 py-2.5 bg-slate-200 text-slate-700 rounded-full font-medium hover:bg-slate-300" data-testid="btn-close-modal">Cancel</button>
            </div>
            {loadingDraft && <p className="text-xs text-slate-400 text-center">Loading draft...</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
